import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import WelcomeHeader from '../components/layout/WelcomeHeader';
import styles from './Welcome.module.css';
import screenshotDetailDe from '../assets/screenshots/screenshot-detail-de.png';
import screenshotDetailEn from '../assets/screenshots/screenshot-detail-en.png';
import screenshotShelfDe from '../assets/screenshots/screenshot-shelf-de.png';
import screenshotShelfEn from '../assets/screenshots/screenshot-shelf-en.png';
import screenshotWishlistDe from '../assets/screenshots/screenshot-wishlist-de.png';
import screenshotWishlistEn from '../assets/screenshots/screenshot-wishlist-en.png';

const ShelfIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="2" width="4" height="20" rx="1"/><rect x="10" y="2" width="4" height="20" rx="1"/>
    <rect x="17" y="2" width="4" height="20" rx="1"/><line x1="1" y1="22" x2="23" y2="22"/>
  </svg>
);
const WishlistIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z"/>
  </svg>
);
const DiscoverIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
  </svg>
);
const FriendsIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
    <circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
    <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
  </svg>
);
const ScanIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M3 7V5a2 2 0 0 1 2-2h2"/><path d="M17 3h2a2 2 0 0 1 2 2v2"/>
    <path d="M21 17v2a2 2 0 0 1-2 2h-2"/><path d="M7 21H5a2 2 0 0 1-2-2v-2"/>
    <line x1="7" y1="12" x2="17" y2="12"/>
  </svg>
);
const StatsIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/>
    <line x1="6" y1="20" x2="6" y2="14"/><line x1="2" y1="20" x2="22" y2="20"/>
  </svg>
);
const ArrowIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12h14"/><path d="m12 5 7 7-7 7"/>
  </svg>
);
const BellIcon = () => (
  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
    <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
  </svg>
);

const BrowserFrame = ({ src, alt, className }) => (
  <div className={`${styles.browserFrame} ${className || ''}`}>
    <div className={styles.browserBar}>
      <span className={styles.browserDot} style={{ background: '#ff5f57' }} />
      <span className={styles.browserDot} style={{ background: '#febc2e' }} />
      <span className={styles.browserDot} style={{ background: '#28c840' }} />
      <span className={styles.browserUrl}>mangashelf.de</span>
    </div>
    <img src={src} alt={alt} className={styles.browserImg} loading="lazy" />
  </div>
);

function Welcome({ loggedIn = false, username = '' }) {
  const { t, i18n } = useTranslation();
  const isEn = i18n.language?.startsWith('en');
  const screenshotDetail = isEn ? screenshotDetailEn : screenshotDetailDe;
  const screenshotShelf = isEn ? screenshotShelfEn : screenshotShelfDe;
  const screenshotWishlist = isEn ? screenshotWishlistEn : screenshotWishlistDe;
  const navigate = useNavigate();

  const FEATURES = [
    { icon: <ShelfIcon />, color: '#7c3aed', title: t('welcome.feature1Title'), desc: t('welcome.feature1Desc') },
    { icon: <WishlistIcon />, color: '#ec4899', title: t('welcome.feature2Title'), desc: t('welcome.feature2Desc') },
    { icon: <BellIcon />, color: '#f97316', title: t('welcome.feature3Title'), desc: t('welcome.feature3Desc') },
    { icon: <DiscoverIcon />, color: '#10b981', title: t('welcome.feature4Title'), desc: t('welcome.feature4Desc') },
    { icon: <FriendsIcon />, color: '#f59e0b', title: t('welcome.feature5Title'), desc: t('welcome.feature5Desc') },
    { icon: <ScanIcon />, color: '#3b82f6', title: t('welcome.feature6Title'), desc: t('welcome.feature6Desc') },
    { icon: <StatsIcon />, color: '#a78bfa', title: t('welcome.feature7Title'), desc: t('welcome.feature7Desc') },
  ];

  const STEPS = [
    { n: '01', title: t('welcome.step1Title'), desc: t('welcome.step1Desc') },
    { n: '02', title: t('welcome.step2Title'), desc: t('welcome.step2Desc') },
    { n: '03', title: t('welcome.step3Title'), desc: t('welcome.step3Desc') },
  ];

  const HIGHLIGHTS = [
    { val: t('welcome.highlight1Val'), label: t('welcome.highlight1Label'), sub: t('welcome.highlight1Sub') },
    { val: t('welcome.highlight2Val'), label: t('welcome.highlight2Label'), sub: t('welcome.highlight2Sub') },
    { val: t('welcome.highlight3Val'), label: t('welcome.highlight3Label'), sub: t('welcome.highlight3Sub') },
    { val: t('welcome.highlight4Val'), label: t('welcome.highlight4Label'), sub: t('welcome.highlight4Sub') },
  ];

  // Hash-Scrolling: /#features direkt aufrufen oder nach Navigation
  useEffect(() => {
    if (window.location.hash === '#features') {
      setTimeout(() => {
        document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, []);

  const scrollToFeatures = () => {
    document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className={styles.page}>
      <WelcomeHeader loggedIn={loggedIn} username={username} />

      {/* HERO */}
      <section className={styles.hero}>
        <div className={styles.heroOrb1} />
        <div className={styles.heroOrb2} />
        <div className={styles.heroOrb3} />
        <div className={styles.heroGrid} />

        <div className={styles.heroContent}>
          <div className={styles.heroLogo}>
            <img src={require('../assets/logo_1.png')} alt="MangaShelf" />
          </div>

          {loggedIn ? (
            <>
              <div className={styles.welcomeBadge}>
                <span>👋</span>&nbsp;{t('welcome.welcomeBack')} <strong>{username}</strong>!
              </div>
              <h1 className={styles.heroTitle}>
                {t('welcome.heroTitleLoggedIn1')}<br /><span className={styles.accent}>{t('welcome.heroTitleLoggedIn2')}</span>
              </h1>
              <p className={styles.heroSub}>
                {t('welcome.heroSubLoggedIn')}
              </p>
              <div className={styles.heroButtons}>
                <button className={styles.btnPrimary} onClick={() => navigate('/mein-regal')}>
                  <ShelfIcon /> {t('welcome.openShelf')}
                </button>
                <button className={styles.btnSecondary} onClick={() => navigate('/discover')}>
                  <DiscoverIcon /> {t('welcome.discoverBtn')}
                </button>
                <button className={styles.btnGhost} onClick={() => navigate('/add-volume')}>
                  {t('welcome.addVolumeBtn')}
                </button>
              </div>
            </>
          ) : (
            <>
              <h1 className={styles.heroTitle}>
                {t('welcome.heroTitle1')}<br /><span className={styles.accent}>{t('welcome.heroTitle2')}</span>
              </h1>
              <p className={styles.heroSub}>
                {t('welcome.heroSub')}
              </p>
              <div className={styles.heroButtons}>
                <button className={styles.btnPrimary} onClick={() => navigate('/register')}>
                  {t('welcome.getStarted')} <ArrowIcon />
                </button>
                <button className={styles.btnSecondary} onClick={scrollToFeatures}>
                  {t('welcome.seeFeatures')}
                </button>
              </div>
              <p className={styles.heroHint}>
                {t('welcome.alreadyRegistered')}{' '}
                <Link to="/login" className={styles.heroLink}>{t('welcome.signIn')}</Link>
              </p>
            </>
          )}
        </div>

        <button className={styles.scrollBtn} onClick={scrollToFeatures} aria-label="Scroll">
          <div className={styles.scrollMouse}><div className={styles.scrollDot} /></div>
        </button>
      </section>

      {/* SCREENSHOT SHOWCASE */}
      <section className={styles.showcaseSection}>
        <div className={styles.showcaseHero}>
          <BrowserFrame src={screenshotShelf} alt="MangaShelf shelf with your own manga collection" />
        </div>

        <div className={styles.showcaseGrid}>
          <div className={styles.showcaseItem}>
            <BrowserFrame src={screenshotWishlist} alt="MangaShelf wishlist with direct shop links" />
            <h3 className={styles.showcaseTitle}>{t('welcome.showcase1')}</h3>
            <p className={styles.showcaseDesc}>{t('welcome.showcase1Desc')}</p>
          </div>
          <div className={styles.showcaseItem}>
            <BrowserFrame src={screenshotDetail} alt="MangaShelf manga detail view with collection and reading progress" />
            <h3 className={styles.showcaseTitle}>{t('welcome.showcase2')}</h3>
            <p className={styles.showcaseDesc}>{t('welcome.showcase2Desc')}</p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className={styles.featuresSection} id="features">
        <div className={styles.sectionHead}>
          <span className={styles.sectionBadge}>{t('welcome.featuresBadge')}</span>
          <h2 className={styles.sectionTitle}>{t('welcome.featuresTitle')}</h2>
          <p className={styles.sectionSub}>{t('welcome.featuresSub')}</p>
        </div>
        <div className={styles.featuresGrid}>
          {FEATURES.map((f, i) => (
            <div key={i} className={styles.featureCard} style={{'--clr': f.color}}>
              <div className={styles.featureIconWrap}>{f.icon}</div>
              <h3 className={styles.featureTitle}>{f.title}</h3>
              <p className={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* SCHRITTE */}
      <section className={styles.stepsSection}>
        <div className={styles.sectionHead}>
          <span className={styles.sectionBadge}>{t('welcome.stepsBadge')}</span>
          <h2 className={styles.sectionTitle}>{t('welcome.stepsTitle')}</h2>
        </div>
        <div className={styles.stepsGrid}>
          {STEPS.map((s, i) => (
            <div key={i} className={styles.stepCard}>
              <div className={styles.stepNum}>{s.n}</div>
              <h3 className={styles.stepTitle}>{s.title}</h3>
              <p className={styles.stepDesc}>{s.desc}</p>
              {i < STEPS.length - 1 && <div className={styles.stepArrow}><ArrowIcon /></div>}
            </div>
          ))}
        </div>
      </section>

      {/* HIGHLIGHTS */}
      <section className={styles.highlightsSection}>
        <div className={styles.highlightsGrid}>
          {HIGHLIGHTS.map((h, i) => (
            <div key={i} className={styles.highlightCard}>
              <span className={styles.highlightVal}>{h.val}</span>
              <span className={styles.highlightLabel}>{h.label}</span>
              <span className={styles.highlightSub}>{h.sub}</span>
            </div>
          ))}
        </div>
      </section>

      {/* CTA – nur für nicht-eingeloggte */}
      {!loggedIn && (
        <section className={styles.ctaSection}>
          <div className={styles.ctaOrb} />
          <div className={styles.ctaContent}>
            <img src={require('../assets/logo_1.png')} alt="" className={styles.ctaLogo} />
            <h2 className={styles.ctaTitle}>{t('welcome.ctaTitle')}</h2>
            <p className={styles.ctaDesc}>{t('welcome.ctaDesc')}</p>
            <div className={styles.ctaButtons}>
              <button className={styles.btnPrimary} onClick={() => navigate('/register')}>
                {t('welcome.ctaStart')} <ArrowIcon />
              </button>
              <button className={styles.btnSecondary} onClick={() => navigate('/login')}>
                {t('welcome.ctaLogin')}
              </button>
            </div>
          </div>
        </section>
      )}

      {/* Nudge für eingeloggte User */}
      {loggedIn && (
        <section className={styles.loggedCta}>
          <p>{t('welcome.loggedCta')}&nbsp;
            <button className={styles.inlineBtn} onClick={() => navigate('/discover')}>
              {t('welcome.loggedCtaBtn')}
            </button>
          </p>
        </section>
      )}
    </div>
  );
}

export default Welcome;
